import React from 'react';
import { Link } from 'react-router-dom';
import { QuizApiClient } from '@/infrastructure/quiz/QuizApiClient';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { getLocaleDateFormatTag } from '@/i18n/localeCatalog';
import { getGuestAttemptId } from '@/utils/guestAttemptId';
import { enrichQuizHistoryRows } from '@/utils/quizSubmitDetailCache';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

function fmtDuration(sec, t) {
  const n = Math.max(0, Number(sec || 0));
  const m = Math.floor(n / 60);
  const s = n % 60;
  return t('common.durationFmt', { m: String(m), s: String(s).padStart(2, '0') });
}

export default function QuizHistoryPage({ session }) {
  const { t, locale } = useAppTranslation();
  const api = React.useMemo(() => new QuizApiClient(), []);
  const token = session?.accessToken || null;
  const guestId = !token ? getGuestAttemptId() : '';
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [items, setItems] = React.useState([]);

  const fmtDate = React.useCallback(
    (ts) => {
      try {
        return new Date(Number(ts || 0)).toLocaleString(getLocaleDateFormatTag(locale));
      } catch {
        return 'N/A';
      }
    },
    [locale],
  );

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    const req = token ? api.getMyQuizResults(token) : guestId ? api.getGuestQuizResults(guestId) : Promise.resolve({ items: [] });
    req
      .then((data) => {
        if (!mounted) return;
        const raw = Array.isArray(data?.items) ? data.items : [];
        setItems(enrichQuizHistoryRows(raw));
      })
      .catch((e) => setError(formatApiErrorMessage(e?.message, t) || t('errors.loadHistory')))
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [api, token, guestId, t]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="font-headline text-3xl font-extrabold">{t('history.pageTitle')}</h1>
        <p className="mt-2 text-sm text-slate-600">
          {t('history.subtitle')}{' '}
          <span className="font-semibold text-slate-800">{t('history.subtitleBold')}</span>{' '}
          {t('history.subtitleEnd')}
        </p>
      </div>
      {!token && !guestId ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">{t('history.noGuest')}</div>
      ) : null}
      {loading ? <p className="text-sm text-slate-600">{t('history.loading')}</p> : null}
      {error ? <p className="text-sm font-semibold text-rose-700">{error}</p> : null}
      {!loading && items.length === 0 ? (
        <div className="rounded-2xl border border-slate-100 bg-white p-6 text-sm text-slate-600 shadow-sm">{t('history.empty')}</div>
      ) : null}

      <div className="space-y-3">
        {items.map((row) => {
          const canOpenDetail = Array.isArray(row.question_results) && row.question_results.length > 0;
          return (
            <article key={row.id} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-bold text-slate-900">{row.quiz_title || `Quiz ${row.quiz_id}`}</h2>
                  <p className="mt-1 text-xs text-slate-500">{t('history.resultId', { id: row.id })}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-bold text-cyan-700">
                    {row.score}% ({row.correct_answers}/{row.total_questions})
                  </span>
                  {canOpenDetail ? (
                    <Link
                      to={`/history/${encodeURIComponent(String(row.id))}`}
                      className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#004ac6] hover:text-[#004ac6]"
                      title={t('history.detail')}
                    >
                      <span className="material-symbols-outlined !text-base" aria-hidden>
                        open_in_new
                      </span>
                      {t('history.detail')}
                    </Link>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-400"
                      title={t('history.detailDisabledTitle')}
                    >
                      <span className="material-symbols-outlined !text-base" aria-hidden>
                        block
                      </span>
                      {t('history.detail')}
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-2 grid gap-2 text-sm text-slate-600 md:grid-cols-3">
                <p>{t('history.at', { time: fmtDate(row.timestamp) })}</p>
                <p>{t('history.duration', { value: fmtDuration(row.time_taken, t) })}</p>
                <p>{t('history.passMark', { pct: Number(row.pass_score || 70) })}</p>
              </div>
              {row.share_code ? (
                <p className="mt-2 text-sm">
                  <Link
                    to={`/play/${encodeURIComponent(row.share_code)}`}
                    className="font-semibold text-[#004ac6] underline decoration-[#004ac6]/30 hover:decoration-[#004ac6]"
                  >
                    {t('history.reopenQuiz')}
                  </Link>
                </p>
              ) : null}

              {!canOpenDetail ? (
                <p className="mt-4 rounded-xl border border-amber-100 bg-amber-50/80 px-4 py-3 text-sm text-amber-950">{t('history.snapshotHint')}</p>
              ) : null}
            </article>
          );
        })}
      </div>
    </div>
  );
}
